/**
 * Formata um número para uma string com duas casas decimais.
 * @param value O número a ser formatado.
 * @returns A string formatada (ex: 123.45).
 */
const formatValue = (value: number): string => {
  return value.toFixed(2).toString();
};

/**
 * Adiciona o tamanho e o valor de um campo ao payload do PIX.
 * @param id O identificador do campo (ex: '00' para PayloadFormatIndicator).
 * @param value O valor do campo.
 * @returns A string formatada para o payload (ex: '000201').
 */
const formatField = (id: string, value: string): string => {
  const size = value.length.toString().padStart(2, '0');
  return `${id}${size}${value}`;
};

/**
 * Normaliza um texto para o padrão PIX: remove acentos, caracteres especiais,
 * converte para maiúsculas.
 * @param text O texto a ser normalizado.
 * @returns O texto normalizado.
 */
const normalizeText = (text: string): string => {
  if (!text) return "";
  return text
    .normalize("NFD") // Separa os acentos das letras
    .replace(/[\u0300-\u036f]/g, "") // Remove os acentos
    .replace(/[^a-zA-Z0-9 ]/g, "") // Remove caracteres não alfanuméricos (exceto espaço)
    .toUpperCase(); // Converte para maiúsculas
};

/**
 * Gera o payload para um QR Code PIX estático.
 * @param pixKey A chave PIX do recebedor.
 * @param recipientName O nome do recebedor.
 * @param recipientCity A cidade do recebedor.
 * @param amount O valor da transação.
 * @param txid O identificador da transação (opcional, use '***' para PIX estático).
 * @returns A string completa do payload "PIX Copia e Cola".
 */
export const gerarPayloadPix = (
  pixKey: string,
  recipientName: string,
  recipientCity: string,
  amount: number,
  txid: string = '***'
): string => {
  // Normaliza e limita o tamanho dos campos de texto
  const normalizedName = normalizeText(recipientName).substring(0, 25);
  const normalizedCity = normalizeText(recipientCity).substring(0, 15);

  const payload = [
    formatField('00', '01'), // Payload Format Indicator
    formatField('26',
      formatField('00', 'br.gov.bcb.pix') +
      formatField('01', pixKey)
    ), // Merchant Account Information
    formatField('52', '0000'), // Merchant Category Code
    formatField('53', '986'), // Transaction Currency (BRL)
    formatField('54', formatValue(amount)), // Transaction Amount
    formatField('58', 'BR'), // Country Code
    formatField('59', normalizedName), // Recipient Name (Normalizado)
    formatField('60', normalizedCity), // Recipient City (Normalizado)
    formatField('62', formatField('05', txid)), // Additional Data Field (txid)
  ].join('');

  const payloadComCrc = payload + '6304'; // Adiciona o campo do CRC16
  const crc = calculaCrc16(payloadComCrc);

  return payloadComCrc + crc;
};

/**
 * Calcula o CRC16-CCITT para o payload do PIX.
 * @param payload O payload sem o CRC.
 * @returns O valor do CRC formatado com 4 caracteres.
 */
function calculaCrc16(payload: string): string {
  let crc = 0xFFFF;
  const polynomial = 0x1021;

  for (let i = 0; i < payload.length; i++) {
    const byte = payload.charCodeAt(i);
    crc ^= (byte << 8);
    for (let j = 0; j < 8; j++) {
      if ((crc & 0x8000) !== 0) {
        crc = (crc << 1) ^ polynomial;
      } else {
        crc <<= 1;
      }
    }
  }

  return (crc & 0xFFFF).toString(16).toUpperCase().padStart(4, '0');
}

/**
 * Converte uma string de moeda formatada (ex: "R$ 1.234,56") para um número.
 * @param value A string de moeda.
 * @returns O valor como um número (ex: 1234.56).
 */
export const desmascararMoeda = (value: string | number | undefined): number => {
  if (value === undefined || value === null) return 0;
  if (typeof value === 'number') return value;

  const stringValue = String(value)
    .replace("R$", "")      // Remove o "R$"
    .replace(/\./g, "")     // Remove os pontos (separador de milhar)
    .replace(",", ".")      // Troca a vírgula (decimal) por ponto
    .trim();                // Remove espaços em branco

  const numberValue = parseFloat(stringValue);

  return isNaN(numberValue) ? 0 : numberValue;
};